import moment from 'moment';

/** @ignore */
export function injectPageViewTracker($rootScope, EventTracker) {
	'ngInject';
	let referrer = window.REFERRER;
	// on every ui-router state change, track the page view
	$rootScope.$on('$stateChangeSuccess', (event, toState, toParams, fromState, fromParams) => {
		const route = toState.url;
		EventTracker.SharprEventTracker.setPageName(toState.pageName || toState.name);
		EventTracker.trackPageView({ referrer, route });
		// wait 300ms to let URL fully update
		setTimeout(() => {
			referrer = window.location.href;
		}, 300);
	});
}

/**
 * Class to track user action events
 */
export class EventTrackerService {
	/**
	 * @param {IdentityService} Identity
	 * @param {$rootScope} $rootScope
	 */
	constructor(Identity, $rootScope, SharprEventTracker) {
		'ngInject';
		/**
		 * @type {IdentityService}
		 */
		this.Identity = Identity;
		/**
		 * @type {$rootScope}
		 */
		this.$rootScope = $rootScope;
		/**
		 * Queue of events, max of 100 items
		 * @type {Array}
		 * @private
		 */
		this._events = [];
		/**
		 * The current user fetched from the Identity service
		 * @type {Object}
		 * @private
		 */
		this._user = null;
		/**
		 * The Sharpr Event Tracker Object
		 * @type {SharprEventTrackerService}
		 */
		this.SharprEventTracker = SharprEventTracker;
		this._attachListeners();
	}

	/**
	 * Get the last entries
	 * @param {Number} numEntries  The number of entries to get
	 * @returns {Array}
	 */
	getHistory(numEntries) {
		return this._events.slice(-1 * numEntries);
	}

	/**
	 * Attach listeners to click and focus
	 * @private
	 */
	_attachListeners() {
		angular.element(document).on('click', event => {
			this.logBrowserEvent('click', event);
		});

		window.addEventListener(
			'focus',
			event => {
				if (!/^(input|textarea|select)$/i.test(event.target.tagName)) {
					return;
				}
				this.logBrowserEvent('focus', event);
			},
			true
		);
	}

	/**
	 * Track that a page was viewed
	 * @param {Object} view  Details about the view
	 * @property {String} [url]  The full URL (defaults to window.location.href)
	 * @property {String} [route]  The angular route URL (e.g. /post/:id/edit)
	 * @property {String} [title]  The page title (defaults to the document title)
	 * @property {String} [referrer]  The referring URL
	 * @property {String} [isModal]  true if page was opened in a modal
	 */
	trackPageView({ url, route, title, referrer, isModal }) {
		// wait 300ms to allow params and window title to be set
		setTimeout(() => {
			if (!url) {
				url = window.location.href;
			}
			if (!title) {
				title = document.title.replace(/ :: .+$/, '');
			}
			const details = {
				url,
				title,
				route,
				is_modal: isModal || false,
			};
			this.SharprEventTracker.track('pv', details, referrer);
			this.add('navigation', details);
		}, 300);
	}

	/**
	 * Add an event to the history
	 * @param {String} type  The type of event
	 * @param {Object} [data]  Arbitrary data about the event
	 * @return {EventTracker}
	 * @chainable
	 */
	add(type, data = {}) {
		let timestamp = moment().format();
		// console.log('event:', type, data);
		this._events.push({ type, data, timestamp });
		if (this._events.length > 100) {
			this._events.shift();
		}
		return this;
	}

	/**
	 * Send event to analytics service and add event to history
	 * @param {String} type  The type of event
	 * @param {Object} [data]  Arbitrary data about the event
	 * @return {EventTracker}
	 * @chainable
	 */
	save(type, data = {}) {
		this.SharprEventTracker.track(type, data);
		this.add(type, data);
		return this;
	}

	/**
	 * Log a DOM or Browser Event
	 * @param {String} type  The type to pass to `add()`
	 * @param {Event} event  The event
	 * @return {EventTracker}
	 * @chainable
	 */
	logBrowserEvent(type, event) {
		try {
			let ancestry = [];
			let node = event.target;
			let ngEvent;
			while (node && node.tagName) {
				let classString = String(node.className || '')
					.replace(/ng-\w+/g, '')
					.trim()
					.replace(/\s+/g, '.');
				if (!ngEvent && node.getAttribute) {
					ngEvent = node.getAttribute('ng-' + type);
				}
				ancestry.unshift(
					node.tagName.toLowerCase() + (node.id ? '#' + node.id : '') + (classString ? '.' + classString : '')
				);
				node = node.parentNode;
			}
			if (ngEvent) {
				event.ngEvent = {
					type: type,
					source: ngEvent,
				};
			}
			this.add(type, {
				path: ancestry,
				pos: this._getPos(event.clientX, event.clientY),
				click: { 0: 'left', 1: 'middle', 2: 'right' }[event.button],
				angular: event.ngEvent,
			});
		} catch (e) {
			// ignore
		}
		return this;
	}

	/**
	 * Get the x,y position relative to the viewport
	 * @param {Number} x
	 * @param {Number} y
	 * @private
	 */
	_getPos(x, y) {
		if (!x || !y) {
			return 'N/A';
		}
		let pctX = Math.round((100 * x) / window.innerWidth);
		let pctY = Math.round((100 * y) / window.innerHeight);
		return `${x},${y} (${pctX}%,${pctY}%)`;
	}
}
